import { StoreId } from "../decorators/StoreIdDecorator";
import {
  AcceleratorDocStatusCalculator,
  doDlfsHaveOcrWarnings,
  doTablesHaveOcrWarnings,
} from "../services/AcceleratorDocStatusCalculator";
import {
  DocumentStatus,
  getDocumentStatuses,
  isDocumentStatus,
} from "../types/DocumentStatuses";
import { AcceleratorBatchSource } from "./AcceleratorBatchSource";
import { DocFieldConfig } from "./config/DocFieldConfig";
import DocTypeConfig from "./config/DocTypeConfig";
import { ExtractedTable, ExtractedTableDataDto } from "./ExtractedTable";
import { BaseField, BaseFieldData } from "./extraction/BaseField";
import { ExtractedFieldData } from "./extraction/ExtractedFieldData";
import { ExtractedTableData } from "./extraction/ExtractedTableData";
import { DefaultValueMatcher } from "./matchers/DefaultValueMatcher";
import { DlfMatcher } from "./matchers/DlfMatcher";
import { MatcherFactory } from "./matchers/factory/MatcherFactory";
import Model, { ModelData } from "./Model";
import Page, { PageData, PageSpans } from "./Page";
import { DlfReuseGenerator } from "./reuse/DlfReuseGenerator";
import { TableReuseGenerator } from "./reuse/TableReuseGenerator";
import Span from "./Span";
import { TableConfig } from "./config/TableConfig";
import { TableModel, TableModelData } from "./TableModel";
import { replaceErrorFieldsWithBlankOrReuse } from "./extraction/ExtractedFieldCleanup";
import ExtractedCell from "./ExtractedCell";

interface AcceleratorDocStoreKey {
  batchInstanceUuid: string;
  docId: string;
}

export { AcceleratorDocStoreKey };
export { AcceleratorDocData };
interface AcceleratorDocData {
  uuid: string;
  docId: string;
  batchInstanceId: string;
  batchInstanceUuid: string;

  // Classification data
  classifiedModel: ModelData;
  classifiedDocType: string;
  statuses: Array<DocumentStatus>;

  pages: Array<PageData>;

  dlfs: {
    [docType in string]: {
      [fieldName in string]: {
        extraction: BaseFieldData | undefined;
        reuse: BaseFieldData[];
      };
    };
  };
  tables: {
    [docType in string]: {
      [tableName in string]: {
        extraction: ExtractedTableDataDto | undefined;
        reuse: TableModelData[];
      };
    };
  };
}

/**
 * Stores a FieldName against an ExtractedFieldData
 */
type ExtractedTableDataMap = Map<string, ExtractedTableData>;
type ExtractedFieldDataMap = Map<string, ExtractedFieldData>;
type DocTypeMap<P> = Map<string, P>;

export default class AcceleratorDoc {
  @StoreId
  storeId: AcceleratorDocStoreKey;

  uuid: string;
  batchInstanceId: string;

  modelName: string;

  // Classification data
  classifiedModel: ModelData;
  classifiedDocType: string;
  _statuses: Array<DocumentStatus> = [];

  pages: Array<Page> = [];

  dlfs: DocTypeMap<ExtractedFieldDataMap> = new Map<
    string,
    ExtractedFieldDataMap
  >();
  tables: DocTypeMap<ExtractedTableDataMap> = new Map<
    string,
    ExtractedTableDataMap
  >();

  // Set by FE only to maintain Model changed status for DocumentSelectorItem
  hasModelChanged: boolean;

  source: AcceleratorBatchSource = "TRANSACT";

  private _allSpansPromise: Promise<PageSpans[]> | undefined;

  constructor(data: AcceleratorDocData) {
    this.hasModelChanged = false;

    this.storeId = {
      batchInstanceUuid: data.batchInstanceUuid,
      docId: data.docId,
    };
    this.modelName = data.classifiedModel.name;
    this.classifiedDocType = data.classifiedDocType;
    this.classifiedModel = data.classifiedModel;
    this.batchInstanceId = data.batchInstanceId;
    this.uuid = data.uuid;
    this.statuses = data.statuses ?? [];

    this.pages = data.pages
      .map((value) => new Page(value))
      .sort((a: Page, b: Page) => a.pageIndex - b.pageIndex);

    Object.keys(data.dlfs).forEach((docType) => {
      const fieldEfdsMap = new Map<string, ExtractedFieldData>();
      Object.keys(data.dlfs[docType]).forEach((field) => {
        try {
          const f = data.dlfs[docType][field];
          // BaseField without matcherData means Model DLFs have changed. Do not make it undefined here,
          // but let changing the DocType create a new BaseField using the same FieldStatus/valid/transformed
          // propertiese that require reextraction
          const extraction: BaseField | undefined =
            f?.extraction && f?.extraction
              ? new BaseField(f.extraction)
              : undefined;
          // filter BaseFields without valid matcherDatas from Reuse, they don't impact initial DocStatus
          const reuse: BaseField[] = (f?.reuse ?? [])
            .filter((x) => x.matcherData)
            .map((f) => new BaseField(f));
          const efd = new ExtractedFieldData(field, extraction, reuse);
          fieldEfdsMap.set(field, efd);
        } catch (e) {
          console.error(e);
          fieldEfdsMap.set(field, new ExtractedFieldData("", undefined, []));
        }
      });
      this.dlfs.set(docType, fieldEfdsMap);
    });

    Object.keys(data.tables).forEach((docType) => {
      const tableEfdsMap = new Map<string, ExtractedTableData>();
      Object.keys(data.tables[docType]).forEach((table) => {
        try {
          const f = data.tables[docType][table];
          const extraction: ExtractedTable | undefined =
            f?.extraction && f?.extraction?.tableTemplateData
              ? new ExtractedTable(f.extraction)
              : undefined;
          // filter Tables with undefined TableModel
          const reuse: TableModelData[] = (f?.reuse ?? []).filter(
            (x) => x != undefined,
          );
          const efd = new ExtractedTableData(table, extraction, reuse);
          // efd.setExtracted(extraction);
          // efd.reuse = reuse;
          tableEfdsMap.set(table, efd);
        } catch (e) {
          console.error(e);
          tableEfdsMap.set(table, new ExtractedTableData("", undefined, []));
        }
      });
      this.tables.set(docType, tableEfdsMap);
    });
  }

  getModelName(): string {
    return this.modelName;
  }
  setModelName(newName: string) {
    this.modelName = newName;
    if (
      this.classifiedModel &&
      (newName !== "" ||
        (this.classifiedModel.templateType !== "GENERAL" &&
          this.classifiedModel.templateType !== "FORMAT"))
    ) {
      this.classifiedModel.name = newName;
    }
  }
  setAcceleratorBatchSource(source: AcceleratorBatchSource) {
    this.source = source;
  }

  getBatchClassId(): string {
    return this.classifiedModel.batchClassIdentifier;
  }
  set statuses(statuses: DocumentStatus[]) {
    // check that statuses contains only valid DocumentStatus, then set
    const keys = getDocumentStatuses();
    const filtered = statuses.filter((s) => isDocumentStatus(s));
    const sorted = filtered.sort((a, b) => keys.indexOf(a) - keys.indexOf(b));
    this._statuses = Array.from(new Set(sorted).values());
  }

  get statuses() {
    return this._statuses;
  }

  static filterByDocStatus(
    documents: AcceleratorDoc[],
    docStatus: DocumentStatus,
  ): number {
    if (documents == null) return 0;
    // return documents.filter((value: AcceleratorDoc): boolean => value.documentStatus.indexOf(docStatus) > -1).length;
    return 0;
  }

  changeDocType(docTypeConfig: DocTypeConfig, model: Model) {
    // ensure map entries exist for next two functions
    this.ensureDocTypeMapKeyExists(docTypeConfig);
    // ensure the DLFs and Tables have reuse arrays an extracted fields
    // ready for blindly inserting data
    this.ensureDlfsExistForMap(docTypeConfig, model);
    this.ensureTablesExistForMap(docTypeConfig, model);
    this.classifiedDocType = docTypeConfig.typeName;
  }

  private ensureDocTypeMapKeyExists(docTypeConfig: DocTypeConfig) {
    if (!this.dlfs.has(docTypeConfig.typeName)) {
      this.dlfs.set(docTypeConfig.typeName, new Map());
    }
    if (!this.tables.has(docTypeConfig.typeName)) {
      this.tables.set(docTypeConfig.typeName, new Map());
    }
  }

  /**
   * Creates an ExtractedFieldData for each DocFieldConfig for the provided DocTypeConfig.
   */
  private ensureDlfsExistForMap(docTypeConfig: DocTypeConfig, model: Model) {
    const dlfsForDocType = this.dlfs.get(docTypeConfig.typeName)!;
    docTypeConfig.fields.forEach((docFieldConfig) => {
      const field = docFieldConfig.fieldName;
      if (!dlfsForDocType.has(field)) {
        // figure out what the default
        // console.error('No EFD');
        const defaultBaseField = this.createBaseFieldForMatcher(
          docFieldConfig,
          model.getDlfMatcher(field),
        );
        dlfsForDocType.set(
          field,
          new ExtractedFieldData(
            docFieldConfig.fieldName,
            defaultBaseField,
            [],
          ),
        );
      } else {
        const efd = dlfsForDocType.get(field);
        // couldn't find EFD here, wtf ?
        if (!efd) return;

        if (!efd.extraction) {
          // console.error('No BaseField for EFD, add default matcher');
          efd.setExtracted(
            this.createBaseFieldForMatcher(
              docFieldConfig,
              model.getDlfMatcher(field),
            ),
          );
        } else if (!efd.extraction._matcherData) {
          // TODO: test
          // console.error('No Matcher for BaseField EFD');
          const replacementField = this.createBaseFieldForMatcher(
            docFieldConfig,
            model.getDlfMatcher(field),
          );
          replacementField.fieldStatus = efd.extraction.fieldStatus;
          replacementField._validationErrors = new Set(
            efd.extraction.validationTypes,
          );
          replacementField.transformed = efd.extraction.transformed;
          if (efd.extraction.transformed) {
            replacementField.fieldValue = efd.extraction.fieldValue;
            replacementField.transformed = efd.extraction.transformed;
          }
          efd.setExtracted(replacementField);
          efd.getReuse().splice(1);
        }
      }
    });
    model.dlfRules.sort((a, b) => {
      const fieldA = docTypeConfig.getDocFieldConfig(a.fieldName);
      const fieldB = docTypeConfig.getDocFieldConfig(b.fieldName);
      const posA = fieldA?.position ?? a.position;
      const posB = fieldB?.position ?? b.position;
      return posA - posB;
    });
  }

  /**
   * Creates an ExtractedTableData for each DocFieldConfig for the provided DocTypeConfig.
   */
  private ensureTablesExistForMap(docTypeConfig: DocTypeConfig, model: Model) {
    const tablesForDocType = this.tables.get(docTypeConfig.typeName)!;
    docTypeConfig.tables.forEach((tableConfig) => {
      const tableName = tableConfig.name;
      if (!tablesForDocType.has(tableName)) {
        tablesForDocType.set(
          tableName,
          new ExtractedTableData(tableName, undefined, []),
        );
      }
    });
  }

  /**
   * Replaces all DLF extraction values with either a BLANK BaseField, or the next available DLF reuse.
   */
  replaceErrorFieldsWithBlankOrReuse(docTypeConfig: DocTypeConfig) {
    docTypeConfig.fields.forEach((docFieldConfig) => {
      const efd = this.getExtractedFieldData(docFieldConfig);
      if (!efd) {
        return console.log(`No ExtractedFieldData for AcceleratorDoc(${this})`);
      }
      replaceErrorFieldsWithBlankOrReuse(
        efd,
        docFieldConfig.fieldName,
        docFieldConfig.threshold,
      );
    });
  }

  /**
   * Updates the Models DLFs based on the current Documents Extraction Result.
   */
  updateModelDlfsFromExtractionResults(
    docTypeConfig: DocTypeConfig,
    model: Model,
  ) {
    const fields = docTypeConfig.fields.map((f) => f.fieldName);
    const dlfsForDocType = this.dlfs.get(docTypeConfig.typeName)!;
    fields.forEach((field) => {
      if (!dlfsForDocType.has(field)) return;
      // figure out what the default
      const extractedFieldData = dlfsForDocType.get(field)!;
      const extracted = extractedFieldData.getAtIndex();
      const matcher = model.getDlfMatcher(field);
      if (!matcher) {
        console.warn(
          "ExtractedField existed for FieldMatcher that did not exist",
        );
        return;
      }
      if (!extracted) {
        console.warn(
          "FieldMatcher existed for ExtractedField that did not exist",
        );
        return;
      }
      if (extracted.extractedBy(matcher)) {
        console.debug(
          "ExtractedField was already extracted by current Model DLF",
        );
      } else {
        console.debug(
          `Update DLF:${field} FieldMatcher from extracted BaseField`,
        );
        // check if extracted is valid or BLANK. updating BLANK means an empty regex rule is replaced with the
        // default KV rule, as those BLANK dlfs from the API have no associated matcher data.
        if (
          ["MATCH", "REUSE", "EMPTY", "BLANK"].indexOf(extracted.fieldStatus) >
          -1
        ) {
          console.info(
            `Update DLF:${field} from ${JSON.stringify(extracted, null, 2)}`,
          );
          console.log("Set ");
          model.updateDlf(
            MatcherFactory.createDlfMatcher(extracted.matcherData),
          );
        }
      }
    });
  }
  /**
   * Updates the Models DLFs based on the current Documents Extraction Result.
   */
  updateExtractionResultsFromTableModel(
    docTypeConfig: DocTypeConfig,
    model: Model,
  ) {
    const tables = docTypeConfig.tables;
    const tablesForDocType = this.tables.get(docTypeConfig.typeName)!;
    tables.forEach((table) => {
      const tableName = table.name;
      if (!tablesForDocType.has(tableName)) return;
      // figure out what the default
      const extractedTableData = tablesForDocType.get(tableName)!;
      const extracted = extractedTableData.getExtracted();
      const tableModel = model.getTableModel(tableName);
      if (!tableModel) {
        console.warn(
          "ExtractedTable existed for TableModel that did not exist",
        );
        return;
      }
      if (!extracted) {
        console.warn(
          "TableModel existed for ExtractedTable that did not exist",
        );
        // FIXME: insert EMPTY table if start pattern is empty
        extractedTableData.setExtracted(
          ExtractedTable.createTest({
            name: tableName,
            status:
              tableModel.isConfigured() && !tableModel.allowEmpty
                ? "ERROR"
                : "EMPTY",
            tableTemplateData: tableModel.toTableModelData(),
          }),
        );
        return;
      }
      // console.debug('ExtractedTable was already extracted by current TableModel');
      // } else {
      if (!extracted.extractedBy(tableModel)) {
        console.debug(
          `Update TBL:${table} TableModel from extracted ExtractedTable`,
        );
        // FIXME: update ExractedTable to be extracted by table model
        extracted.tableTemplateData = tableModel.toTableModelData();
        extracted.tableModelId = tableModel.getModelId();
        console.debug(
          `Updated table ${extracted.name} to use table model id ${extracted.tableModelId}`,
        );
        // // check if extracted is valid or BLANK. updating BLANK means an empty regex rule is replaced with the
        // // default KV rule, as those BLANK dlfs from the API have no associated matcher data.
        // if (['MATCH', 'REUSE', 'EMPTY', 'BLANK'].indexOf(extracted.fieldStatus) > -1) {
        //     console.info(`Update DLF:${table} from ${JSON.stringify(extracted, null, 2)}`);
        //     console.log('Set ')
        //     model.updateDlf(MatcherFactory.createDlfMatcher(extracted.matcherData));
        // }
      }

      for (const reuseModel of extractedTableData.getReuse()) {
        // get extraced reuse e
        if (
          reuseModel &&
          extractedTableData.fromCache(new TableModel(reuseModel))
        ) {
          const reuseTable = extractedTableData.fromCache(
            new TableModel(reuseModel),
          )!;
          let addedCells = 0;
          for (const row of reuseTable.rows) {
            const missingCells = table.columnNames.filter(
              (x) =>
                row.cells.find((cell) => cell.fieldName === x.fieldName) ==
                undefined,
            );
            if (missingCells.length > 0) {
              row.cells.push(
                ...missingCells.map((x) =>
                  ExtractedCell.createTest({
                    fieldName: x.fieldName,
                  }),
                ),
              );
              addedCells += missingCells.length;
            }
          }
          console.debug(`Added ${addedCells} cells to table ${tableName}`);
        }
      }
    });
  }

  private createBaseFieldForMatcher(
    docFieldConfig: DocFieldConfig,
    dlfMatcher?: DlfMatcher,
  ): BaseField {
    if (!dlfMatcher) {
      return new BaseField({
        fieldName: docFieldConfig.fieldName,
        matcherData:
          MatcherFactory.defaultMatcherFrom(docFieldConfig).toMatcherData(),
        fieldStatus: "BLANK",
        transformed: false,
        validationTypes: [],
        confidence: 0,
        ocrConfidence: -1,
        fieldValue: "",
        pageId: "",
        spanIds: [],
        _requireExtract: false,
      });
    }

    const baseField = new BaseField({
      fieldName: docFieldConfig.fieldName,
      matcherData: dlfMatcher.toMatcherData(),
      fieldStatus: "BLANK",
      ocrConfidence: -1,
      confidence: 0,
      transformed: false,
      validationTypes: [],
      fieldValue: "",
      pageId: "",
      spanIds: [],
      _requireExtract: true, // required reextraction by default
    });
    // figure out if matcher is configured or not
    if (dlfMatcher.isConfigured()) {
      baseField.fieldStatus = "ERROR";
    } else {
      baseField._requireExtract = false;
    }
    // figure out FieldStatus.
    if (dlfMatcher.getDiscriminator() === "F_DEFAULT") {
      // inject FieldStauts, fieldValue
      baseField.fieldValue = (dlfMatcher as DefaultValueMatcher).defaultValue;
      baseField.fieldStatus = "HARDCODE";
      baseField._requireExtract = false; // never require reextraction
    }

    return baseField;
  }

  /**
   * Remove the extraction and reuse information for a DLF.
   * @param field dlf to remove
   */
  clearDlf(field: DocFieldConfig | string) {
    const extractedFieldData = this.getExtractedFieldData(field);
    extractedFieldData.clearExtracted();
    extractedFieldData.setReuse([]);
  }

  async generateDlfReuse(
    field: DocFieldConfig | string,
  ): Promise<Array<BaseField | undefined>> {
    let fieldName: string;
    if (typeof field === "string") {
      fieldName = field;
    } else {
      fieldName = field.fieldName;
    }
    // console.info(`Generating Reuse for DLF:${fieldName}`);

    const resuseResult = await new DlfReuseGenerator(fieldName).calculate(this);

    const extractedFieldData = this.getExtractedFieldData(field);
    const wasBlank = extractedFieldData.getExtracted()?.fieldStatus === "BLANK";
    if (resuseResult) {
      extractedFieldData.setReuse(resuseResult!);
    }

    return resuseResult ?? [];
  }

  async generateTableReuse(
    table: TableConfig | string,
  ): Promise<TableModelData[]> {
    let tableName: string;
    if (typeof table === "string") {
      tableName = table;
    } else {
      tableName = table.name;
    }
    // console.info(`Generating Reuse for TABLE:${tableName}`);

    const reuseResults = await new TableReuseGenerator(
      this.classifiedDocType,
      tableName,
    ).calculate(this);

    const extractedTableData = this.getExtractedTableData(table);
    if (reuseResults) {
      // don't set resue at start here
      extractedTableData.setReuse(reuseResults);
    }

    return reuseResults ?? [];
    // return extractedTableData.getReuse() as TableModelData[];
  }

  /**
   * Recalculate DocumentStatus based off Model and current Extraction Results
   * @deprecated Do not call this method directly, use AcceleratorDoc.caclulateDocumentStatusWithOcr(Model, DocTypeConfig)
   */
  calculateDocumentStatus(model: Model): DocumentStatus[] {
    if (this.dlfs.get(this.classifiedDocType) == undefined) {
      console.warn(
        "Cannot calculate DocumentStatus of AcceleratorDoc without configured ExtractedFieldDataMap",
      );
    }
    return (this.statuses =
      AcceleratorDocStatusCalculator.calculateDocumentStatus({
        isTemplateType: model.templateType,
        unknown: model.unknown,
        wasTemplateType: this.classifiedModel.templateType,
        hasMultiple: this.statuses.includes("MULTIPLE"),
        docTypeEfds: Array.from(
          this.dlfs.get(this.classifiedDocType)?.values() ?? [],
        ),
        docTypeEtds: Array.from(
          this.tables.get(this.classifiedDocType)?.values() ?? [],
        ),
      }));
  }
  /**
   * Recalculate DocumentStatus based off Model and current Extraction Results
   */
  calculateDocumentStatusWithOcr(
    acceleratorOcrThresholds: boolean,
    model: Model,
    docTypeConfig: DocTypeConfig,
  ): DocumentStatus[] {
    const newStatuses = this.calculateDocumentStatus(model);
    if (docTypeConfig.typeName !== this.classifiedDocType) {
      return (this.statuses = newStatuses);
    }
    if (this.dlfs.has(this.classifiedDocType)) {
      const efds = Array.from(this.dlfs.get(this.classifiedDocType)!.values());
      if (
        doDlfsHaveOcrWarnings(
          acceleratorOcrThresholds,
          efds,
          docTypeConfig.fields,
        )
      ) {
        newStatuses.push("OCR_WARNING");
      }
    }
    if (this.tables.has(this.classifiedDocType)) {
      const etds = Array.from(
        this.tables.get(this.classifiedDocType)!.values(),
      );
      if (
        doTablesHaveOcrWarnings(
          acceleratorOcrThresholds,
          etds,
          docTypeConfig.tables,
        )
      ) {
        newStatuses.push("OCR_WARNING");
      }
    }
    return (this.statuses = newStatuses);
  }

  /**
   * Updates the extraction result for the provided field for the current DocType.
   */
  updateExtractedField(extractedField: BaseField) {
    const docTypeFields = this.dlfs.get(this.classifiedDocType)!;

    docTypeFields.get(extractedField.fieldName)!.setExtracted(extractedField);
  }
  /**
   * Updates the extraction result for the provided field for the current DocType.
   */
  updateExtractedTable(extractedTable: ExtractedTable) {
    const docTypeFields = this.tables.get(this.classifiedDocType)!;

    docTypeFields.get(extractedTable.name)!.setExtracted(extractedTable);
  }

  getExtractedFieldData(field: DocFieldConfig | string): ExtractedFieldData {
    let fieldName: string;
    if (typeof field === "string") {
      fieldName = field;
    } else {
      fieldName = field.fieldName;
    }

    if (!this.dlfs.has(this.classifiedDocType)) {
      this.dlfs.set(this.classifiedDocType, new Map());
    }

    const docTypeData = this.dlfs.get(this.classifiedDocType!)!;

    if (!docTypeData.has(fieldName)) {
      docTypeData.set(
        fieldName,
        new ExtractedFieldData(fieldName, undefined, []),
      );
    }

    return docTypeData.get(fieldName)!;
  }

  getExtractedTableData(table: TableConfig | string): ExtractedTableData {
    let tableName: string;
    if (typeof table === "string") {
      tableName = table;
    } else {
      tableName = table.name;
    }

    if (!this.tables.has(this.classifiedDocType)) {
      this.tables.set(this.classifiedDocType, new Map());
    }

    const docTypeData = this.tables.get(this.classifiedDocType!)!;
    if (!docTypeData.has(tableName)) {
      docTypeData.set(
        tableName,
        new ExtractedTableData(tableName, undefined, []),
      );
    }

    return docTypeData.get(tableName)!;
  }

  getPage(index: number): Page | undefined {
    return this.pages[index];
  }
  getPageById(id: string): Page | undefined {
    return this.pages.find((x) => x.externalPageIdentifier === id);
  }
  getPageIndex(id: string): number {
    return this.pages.findIndex((x) => x.externalPageIdentifier === id);
  }

  async getPageSpans(index: number): Promise<PageSpans> {
    const page: Page | undefined = this.getPage(index);
    if (page) {
      return await page.getSpans(
        this.storeId.docId,
        this.storeId.batchInstanceUuid,
        index,
      );
    }
    throw new Error(`Page at index (${index}) could not be found`);
  }
  async getPageSpansById(pageId: string): Promise<PageSpans> {
    const page: Page | undefined = this.getPageById(pageId);
    if (page) {
      return await page.getSpans(
        this.storeId.docId,
        this.storeId.batchInstanceUuid,
        page.pageIndex,
      );
    }
    throw new Error(`Page with Id ${pageId} could not be found`);
  }

  async getAllPageSpans(): Promise<PageSpans[]> {
    if (!this._allSpansPromise) {
      const storeId = { ...this.storeId };
      this._allSpansPromise = Promise.all(
        this.pages.map((page, pIndex) =>
          page.getSpans(storeId.docId, storeId.batchInstanceUuid, pIndex),
        ),
      );
    }
    return await this._allSpansPromise;
  }

  async getSpansById(spanId: string): Promise<Span> {
    return (
      (await this.getAllPageSpans())
        .flatMap((x) => x.spans)
        .filter((x) => x.id == spanId)[0] ?? null
    );
  }

  toExtractData(): AcceleratorDocData {
    return {
      docId: this.storeId.docId,
      batchInstanceId: this.batchInstanceId,
      batchInstanceUuid: this.storeId.batchInstanceUuid,
      classifiedDocType: this.classifiedDocType,
      classifiedModel: this.classifiedModel,
      dlfs: {},
      tables: {},
      pages: this.pages.map((x) => x.toData()),
      statuses: [],
      uuid: "",
    };
  }

  static createTest(rest: Partial<AcceleratorDocData> = {}): AcceleratorDoc {
    const defaultAccDoc: AcceleratorDocData = {
      uuid: "",
      docId: "",
      batchInstanceId: "",
      batchInstanceUuid: "",
      classifiedDocType: "",
      classifiedModel: Model.createTest().toModelData(),
      statuses: [],
      pages: [],
      dlfs: {},
      tables: {},
    };

    return new AcceleratorDoc({
      ...defaultAccDoc,
      ...rest,
    });
  }
}
